import { useCallback, useState } from 'react';
import StandardWizard from '../../Wizard';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { CheckBox, Error } from '@mui/icons-material';
import { useFeedback } from '../../../utils/FeedbackContext';

const ExampleComponent = ({ variant, text }) => {
  return (
    <Box
      p={1}
      fullWidth
      display="flex"
      flexDirection="row"
      gap={2}
      alignItems="center"
    >
      {variant === 'green' && <CheckBox color="success" />}
      {variant === 'red' && <Error color="error" />}
      {variant === 'neutral' && <Error color="action" />}
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
};

export const ClientWizard = ({
  title,
  disabled,
  variant = 'add',
  buttonLabel,
  onChange,
  required = false,
}) => {
  const [step, setStep] = useState(1);
  const [showWizard, setWizard] = useState(false);
  const [numClients, setNumClients] = useState(1);
  const { notifySuccess } = useFeedback();
  const [clients, setClients] = useState([]);

  const validClients =
    clients.length === numClients &&
    clients.every(
      (client) =>
        typeof client === 'string' &&
        client.trim().length > 0 &&
        client.trim().split(' ').length >= 2
    );

  const ActionComp = useCallback(
    () => (
      <>
        {step === 1 && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            p={2}
            borderTop={1}
            width="100%"
          >
            <Typography variant="h6" color={'darkslategray'}>
              Are you ready to provide client information?
            </Typography>
            <Box display="flex" flexDirection="row" gap={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setStep(2);
                }}
              >
                <CheckBox color="success" />
                Yes
              </Button>
            </Box>
          </Box>
        )}
        {step === 2 && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            p={2}
            borderTop={1}
            width="100%"
          >
            <Box display="flex" flexDirection="row" gap={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setStep(1);
                  setWizard(false);
                  onChange(clients.join(', '));
                  notifySuccess(`Clients saved: ${clients.join(', ')}`);
                }}
                disabled={!validClients}
              >
                <CheckBox color="success" />
                Save Clients
              </Button>
            </Box>
          </Box>
        )}
      </>
    ),
    [step, onChange, notifySuccess, validClients, clients]
  );
  return (
    <StandardWizard
      title={title}
      disabled={disabled}
      variant={variant}
      buttonLabel={buttonLabel}
      onChange={onChange}
      required={required}
      step={step}
      setStep={setStep}
      ActionComponent={() => <ActionComp />}
      showSteps={true}
      totalSteps={2}
      showWizard={showWizard}
      setWizard={setWizard}
    >
      {step === 1 && (
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            A client is the individual (or individuals) that defined the project
            topic and will be supervising and meeting with you throughout the
            project period to discuss the project details.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You project involves a client? Yes if...
          </Typography>

          <ExampleComponent
            variant="green"
            text="A CECS or UM-Dearborn faculty or member that defined the project topic.."
          />
          <ExampleComponent
            variant="green"
            text="An industry or non-university individual that defined the project topics."
          />
          <Typography variant="body2" color="textSecondary">
            The following are{' '}
            <span style={{ color: 'red', fontSize: '1.05em' }}>not</span>{' '}
            considered clients:
          </Typography>
          <ExampleComponent
            variant="red"
            text="Who the product is for, e.g. 'students' or 'people with disability'."
          />

          <ExampleComponent
            variant="red"
            text="Your professor, e.g. 'Dr. Smith'. Unless they are sponsoring the project."
          />
          <ExampleComponent
            variant="red"
            text="Your senior design course instructor unless they are defining the project topic."
          />
          <ExampleComponent
            variant="red"
            text="The students that are members of your team"
          />
        </Box>
      )}
      {step === 2 && (
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            Please provide the client details.
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            You have selected "Yes" for having a client. If you do not have a
            client, please return to the previous step and select "No".
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Here are some examples of what a client might look like:
            <ExampleComponent variant="green" text="John Doe" />
            <ExampleComponent variant="green" text="Marry Smith" />
            <ExampleComponent
              variant="neutral"
              text="Its first name then last name"
            />
            <Typography variant="body2" color="textSecondary">
              How many clients do you have?
            </Typography>
            <br />
            <TextField
              value={numClients}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!isNaN(value) && value > 0) {
                  setNumClients(value);
                }
              }}
              type="number"
              label="Number of Clients"
              variant="outlined"
              fullWidth
              required
              inputProps={{ min: 1, max: 4 }}
            />
          </Typography>
          {Array.from({ length: numClients }, (_, index) => (
            <Box key={index} mt={2}>
              <TextField
                fullWidth
                label={`Client Name (${index + 1})`}
                variant="filled"
                required
                value={clients[index] || ''}
                onChange={(e) => {
                  const newClients = [...clients];
                  newClients[index] = e.target.value
                    .split(' ')
                    .map(
                      (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                    )
                    .join(' ');

                  setClients(newClients);
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </StandardWizard>
  );
};

export const ProjectIdentityWizard = ({
  title,
  disabled,
  variant = 'add',
  buttonLabel,
  onChange,
  required = false,
}) => {
  const [step, setStep] = useState(1);
  const [showWizard, setWizard] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const { notifySuccess } = useFeedback();

  const ActionComp = useCallback(
    () => (
      <>
        {step === 1 && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            p={2}
            borderTop={1}
            width="100%"
          >
            <Box display="flex" flexDirection="row" gap={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  onChange(projectName, projectDesc);
                  setStep(1);
                  setWizard(false);
                  notifySuccess(
                    `Project Title & Description saved: ${projectName}, ${projectDesc}`
                  );
                }}
              >
                Save Title & Description
              </Button>
            </Box>
          </Box>
        )}
      </>
    ),
    [step, projectName, projectDesc, onChange, notifySuccess]
  );

  return (
    <StandardWizard
      title={title}
      disabled={disabled}
      variant={variant}
      buttonLabel={buttonLabel}
      onChange={onChange}
      required={required}
      step={step}
      setStep={setStep}
      ActionComponent={() => <ActionComp />}
      showSteps={true}
      totalSteps={1}
      showWizard={showWizard}
      setWizard={setWizard}
    >
      {step === 1 && (
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            Please provide the project name.
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            The project name should be concise yet descriptive, giving a clear
            indication of the project's purpose or main objective.
          </Typography>
          <Box mb={2}>
            <ExampleComponent
              variant="green"
              text="E-Commerce Website for Local Businesses"
            />
            <ExampleComponent
              variant="green"
              text="Mobile App for Personal Finance Management"
            />
            <ExampleComponent
              variant="green"
              text="AI-Powered Chatbot for Customer Support"
            />
            <ExampleComponent
              variant="red"
              text="mobile App for personal Finance management (user proper title format)"
            />
            <ExampleComponent variant="red" text="To be decided" />
            <ExampleComponent
              variant="red"
              text="E-Commerce Website for Local Businesses CIS 452 w/ Professor Team 42..."
            />
          </Box>
          <TextField
            fullWidth
            label="Project Name"
            variant="filled"
            required
            value={projectName}
            onChange={(e) => {
              const value = e.target.value;
              setProjectName(value);
            }}
          />
        </Box>
      )}
    </StandardWizard>
  );
};

export const ExpectedNumberOfMembersWizard = ({
  title,
  disabled,
  variant = 'add',
  buttonLabel,
  onChange,
  required = false,
}) => {
  const [step, setStep] = useState(1);
  const [showWizard, setWizard] = useState(false);
  const [numMembers, setNumMembers] = useState(4);
  const { notifySuccess } = useFeedback();

  const ActionComp = useCallback(
    () => (
      <>
        {step === 1 && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            p={2}
            borderTop={1}
            width="100%"
          >
            <Box display="flex" flexDirection="row" gap={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setStep(1);
                  setWizard(false);
                  onChange(numMembers);
                  notifySuccess(
                    `Expected number of members saved: ${numMembers}`
                  );
                }}
                disabled={numMembers < 1 || numMembers > 10}
              >
                <CheckBox color="success" />
                Save Expected Number of Members
              </Button>
            </Box>
          </Box>
        )}
      </>
    ),
    [step, onChange, notifySuccess, numMembers]
  );
  return (
    <StandardWizard
      title={title}
      disabled={disabled}
      variant={variant}
      buttonLabel={buttonLabel}
      onChange={onChange}
      required={required}
      step={step}
      setStep={setStep}
      ActionComponent={() => <ActionComp />}
      showSteps={true}
      totalSteps={1}
      showWizard={showWizard}
      setWizard={setWizard}
    >
      {step === 1 && (
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            Please provide the number of members in your project team
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            This will help provide us an idea of the team size you anticipate
            for your project. The number of members should be between 1 and 6.
          </Typography>
          <Box mb={2} />
          <TextField
            value={numMembers || 4}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value) && value > 0) {
                setNumMembers(value);
              }
            }}
            type="number"
            label="Project Team Mebmbers"
            variant="outlined"
            fullWidth
            required
            inputProps={{ min: 1, max: 10 }}
          />
          <Alert severity="info" sx={{ marginTop: 2 }}>
            Note: The project leader must make sure each team member creates an
            account and is invited to the project.
          </Alert>
        </Box>
      )}
    </StandardWizard>
  );
};
